import { resolveOtherServiceCustomerAmounts } from './otherServicePricing';

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toCents = (value) => Math.round(toNumber(value) * 100);

const normalizeCode = (value) => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const warnTaxBreakdownDiagnostic = (message, payload = {}) => {
  if (process.env.NODE_ENV !== 'development') return;
  // eslint-disable-next-line no-console
  console.warn(`[GeneratedInvoice taxBreakdown] ${message}`, payload);
};

export const normalizeTaxRateDecimal = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1 ? n / 100 : n;
};

const getTaxId = (tax = {}) => tax?.taxId ?? tax?.TaxId ?? tax?.taxID ?? tax?.id ?? null;

const getTaxRate = (tax = {}) => normalizeTaxRateDecimal(tax?.rate ?? tax?.Rate ?? tax?.value ?? tax?.Value);

const getTaxCodeParts = (tax = {}) => {
  const type = normalizeCode(tax?.type ?? tax?.Type ?? tax?.name ?? tax?.label ?? tax?.taxName);
  const stateCode = normalizeCode(tax?.stateCode ?? tax?.StateCode ?? tax?.state ?? tax?.province);
  const countryCode = normalizeCode(tax?.countryCode ?? tax?.CountryCode ?? tax?.country);
  const label = normalizeCode(tax?.label ?? tax?.name ?? tax?.taxName ?? tax?.title);
  const canonical = [type, stateCode, countryCode, label].filter(Boolean).join('');
  return { type, stateCode, countryCode, label, canonical };
};

const component2LabelsByState = {
  PBC: 'BC PST',
  PMB: 'MB PST',
  PSK: 'SK PST',
  PQC: 'QST',
};

const hstCodes = new Set(['H13', 'H14', 'H15']);

const isHstRow = (tax = {}) => {
  const { type, stateCode, canonical } = getTaxCodeParts(tax);
  return hstCodes.has(type) || hstCodes.has(stateCode) || /\bHST\b/.test(canonical) || /^H1[345]$/.test(stateCode);
};

const isCombinedCanadianGstPstRow = (tax = {}) => {
  const rate = getTaxRate(tax);
  const { type, stateCode, canonical } = getTaxCodeParts(tax);
  if (rate <= 0) return false;
  if (stateCode && component2LabelsByState[stateCode]) return true;
  return type === 'GST' && /P(BC|MB|SK|QC)/.test(canonical);
};

const isStandaloneGstTax = (tax = {}) => {
  if (isCombinedCanadianGstPstRow(tax)) return false;
  const rate = getTaxRate(tax);
  const { type, stateCode, canonical } = getTaxCodeParts(tax);
  return type === 'GST' || stateCode === 'GST' || (canonical.includes('GST') && Math.abs(rate - 0.05) < 0.0001);
};

const buildComponent = ({ key, label, rate, sourceTaxId }) => ({
  key,
  label,
  rate,
  sourceTaxId,
});

const findGstRow = (allTaxes = []) => {
  const rows = Array.isArray(allTaxes) ? allTaxes : [];
  return rows.find((tax) => {
    const rate = getTaxRate(tax);
    const { type, stateCode, canonical } = getTaxCodeParts(tax);
    if (rate <= 0) return false;
    if (isCombinedCanadianGstPstRow(tax) || isHstRow(tax)) return false;
    return type === 'GST' || stateCode === 'GST' || (canonical.includes('GST') && Math.abs(rate - 0.05) < 0.0001);
  }) || null;
};

const cleanDisplayLabel = (value) => String(value || '').trim();

const getSingleTaxLabel = (tax = {}) => {
  const { countryCode } = getTaxCodeParts(tax);
  if (isHstRow(tax)) return 'HST';

  const rawType = cleanDisplayLabel(tax?.type ?? tax?.Type ?? tax?.taxName ?? tax?.title);
  if (rawType) return rawType;

  const rawState = cleanDisplayLabel(tax?.stateCode ?? tax?.StateCode ?? tax?.state ?? tax?.province);
  if (rawState) return rawState.toUpperCase();

  if (countryCode) return `${countryCode} Tax`;
  return 'Tax';
};

export const buildTaxComponentResolver = (allTaxes = []) => {
  const taxes = Array.isArray(allTaxes) ? allTaxes : [];
  const gstRow = findGstRow(taxes);
  const gstRate = getTaxRate(gstRow);

  return (tax = {}) => {
    const rate = getTaxRate(tax);
    if (rate <= 0) return [];

    const taxId = getTaxId(tax);
    const { stateCode } = getTaxCodeParts(tax);

    if (isHstRow(tax)) {
      return [buildComponent({ key: 'hst', label: 'HST', rate, sourceTaxId: taxId })];
    }

    if (isCombinedCanadianGstPstRow(tax) && gstRate > 0 && rate > gstRate) {
      const secondaryRate = Math.max(0, rate - gstRate);
      const secondaryLabel = component2LabelsByState[stateCode] || 'PST';
      return [
        buildComponent({ key: 'gst', label: 'GST', rate: gstRate, sourceTaxId: taxId }),
        buildComponent({
          key: `pst-${stateCode || secondaryLabel.toLowerCase().replace(/\s+/g, '-')}`,
          label: secondaryLabel,
          rate: secondaryRate,
          sourceTaxId: taxId,
        }),
      ];
    }

    const label = getSingleTaxLabel(tax);
    return [buildComponent({
      key: label.toLowerCase().replace(/[^a-z0-9]+/g, '-') || `tax-${taxId || 'generic'}`,
      label,
      rate,
      sourceTaxId: taxId,
    })];
  };
};

export const resolveTaxComponentsForTax = (tax, allTaxes = []) => buildTaxComponentResolver(allTaxes)(tax);

const getItemSubtotalCents = (product = {}) => {
  const pricings = Array.isArray(product?.pricings) ? product.pricings : [];
  const artworks = Array.isArray(product?.artworks) ? product.artworks : [];
  const decorations = Array.isArray(product?.decorations) ? product.decorations : [];
  let subtotalCents = 0;

  for (const pr of pricings) {
    const qty = toNumber(pr?.Quantity);
    const retail = toNumber(pr?.Retail);
    const rowTotal = pr?.Total != null ? toNumber(pr.Total, retail * qty) : retail * qty;
    subtotalCents += toCents(rowTotal);
  }

  for (const deco of decorations) {
    const charges = Array.isArray(deco?.Charge) ? deco.Charge : [];
    for (const ch of charges) {
      const chQty = toNumber(ch?.Quantity);
      const chRetail = toNumber(ch?.Retail);
      const fallbackTotal = ch?.Total != null ? toNumber(ch.Total, chRetail * chQty) : chRetail * chQty;
      const chTotal = chRetail > 0 ? chRetail * chQty : fallbackTotal;
      subtotalCents += toCents(chTotal);
    }
  }

  for (let i = 0; i < artworks.length; i += 1) {
    const art = artworks[i] || {};
    if (art?.Total != null) {
      subtotalCents += toCents(toNumber(art.Total));
      continue;
    }
    const charge = toNumber(art?.Charge);
    const matchedQty = toNumber(pricings?.[i]?.Quantity);
    subtotalCents += toCents(charge * matchedQty);
  }

  return subtotalCents;
};

const getOtherServiceSubtotalCents = (svc = {}) => (
  // Match the invoice's customer-facing service pricing. A legacy Subtotal: 0
  // must not override a real RetailPrice, but explicit zero retail remains free.
  toCents(resolveOtherServiceCustomerAmounts(svc).subtotal)
);

const normalizeTaxDetailsList = (row = {}) => {
  if (Array.isArray(row?.taxDetailsList) && row.taxDetailsList.length) return row.taxDetailsList;
  if (row?.taxDetails && typeof row.taxDetails === 'object') return [row.taxDetails];
  return [];
};

const normalizeTaxIdCandidates = (value) => {
  if (value == null || value === '') return [];
  let raw = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        raw = JSON.parse(trimmed);
      } catch (_) {
        raw = trimmed;
      }
    }
  }

  const candidates = Array.isArray(raw)
    ? raw
    : (typeof raw === 'string' && raw.includes(','))
      ? raw.split(',')
      : [raw];

  const ids = [];
  const seen = new Set();
  candidates.forEach((candidate) => {
    const valueToNormalize = candidate && typeof candidate === 'object'
      ? (candidate.taxId ?? candidate.TaxId ?? candidate.taxID ?? candidate.id ?? candidate.value)
      : candidate;
    const id = Number(valueToNormalize);
    if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

const getOtherServiceTaxIds = (svc = {}) => {
  // Shipping has a single selected GST/HST ID. The legacy "Tax" alias can
  // contain a dollar amount, so it must not introduce a second tax row.
  const isShipping = String(svc?.Type ?? svc?.type ?? '').trim().toLowerCase() === 'shipping';
  if (isShipping) {
    const selected = [svc?.TaxId, svc?.taxId, svc?.taxID]
      .flatMap(normalizeTaxIdCandidates).find(Number.isInteger);
    if (selected !== undefined) return [selected];
  }

  const ids = [];
  const seen = new Set();
  const pushIds = (source) => {
    normalizeTaxIdCandidates(source).forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
  };

  // A legacy Shipping.Tax may be a dollar amount (e.g. 5.42).
  const legacyTaxAlias = isShipping && !Number.isInteger(Number(svc?.Tax))
    ? null : svc?.Tax;
  [svc?.taxIds, svc?.TaxIds, svc?.taxId, svc?.taxID, svc?.TaxId, legacyTaxAlias].forEach(pushIds);

  const rawTaxes = svc?.Taxes;
  const canTreatTaxesAsTaxIds =
    Array.isArray(rawTaxes) ||
    (rawTaxes && typeof rawTaxes === 'object') ||
    (typeof rawTaxes === 'string' && /[[\]{},]/.test(rawTaxes));
  if (canTreatTaxesAsTaxIds) pushIds(rawTaxes);

  return ids;
};

const findTaxCatalogMatch = (taxId, allTaxes = []) => {
  const numericTaxId = Number(taxId);
  if (!Number.isFinite(numericTaxId) || numericTaxId <= 0) return null;
  const matched = (Array.isArray(allTaxes) ? allTaxes : []).find((tax) => Number(tax?.taxId) === numericTaxId);
  if (!matched) return null;
  return {
    taxId: matched?.taxId ?? numericTaxId,
    type: matched?.type || matched?.label || matched?.stateCode || matched?.countryCode || 'Tax',
    stateCode: matched?.stateCode || null,
    countryCode: matched?.countryCode || null,
    rate: normalizeTaxRateDecimal(matched?.rate),
  };
};

const buildGenericServiceTax = (svc = {}) => {
  // OtherServices.Taxes is a percentage for every service type:
  // Shipping, Art and Other all store 1 to mean 1%, not 100%.
  const shipping = String(svc?.Type ?? svc?.type ?? '').trim().toLowerCase() === 'shipping';
  const percent = Number(svc?.Taxes);
  const hasStoredPercent = svc?.Taxes != null && svc.Taxes !== '' && Number.isFinite(percent);
  const rate = hasStoredPercent
    ? Math.max(0, percent / 100)
    : normalizeTaxRateDecimal(svc?.Tax ?? svc?.taxRate);
  if (rate <= 0) return null;
  const legacyTaxId = shipping && !Number.isInteger(Number(svc?.Tax))
    ? null : svc?.Tax;
  return {
    taxId: svc?.TaxId ?? svc?.taxId ?? legacyTaxId ?? null,
    // Without a matching catalogue entry, a legacy Shipping taxLabel can
    // still describe the previous rate. Do not guess GST/HST from that label.
    type: shipping ? 'Tax' : (svc?.taxLabel || svc?.TaxLabel || 'Tax'),
    stateCode: null,
    countryCode: null,
    rate,
  };
};

const buildResolvedServiceTaxRows = (svc = {}, allTaxes = []) => {
  const shipping = String(svc?.Type ?? svc?.type ?? '').trim().toLowerCase() === 'shipping';
  const chosen = [svc?.TaxId, svc?.taxId, svc?.taxID]
    .find((id) => id != null && id !== '');
  const selectedId = Number(chosen);
  const hasSelectedId = Number.isInteger(selectedId) && selectedId > 0;
  const hasNonShippingMultiSelection = !shipping &&
    (normalizeTaxIdCandidates(svc?.taxIds).length > 0 ||
      normalizeTaxIdCandidates(svc?.TaxIds).length > 0);
  if (svc?.taxable === false || svc?.isTaxable === false ||
      svc?.taxDetails?.taxable === false ||
      ['exempt', 'non-taxable', 'no-tax', 'none'].includes(
        String(svc?.taxTreatment ?? svc?.TaxTreatment ?? '').trim().toLowerCase()
      ) ||
      (chosen != null && selectedId === 0) ||
      (!hasSelectedId && !hasNonShippingMultiSelection &&
        svc?.Taxes != null && svc?.Taxes !== '' && Number(svc.Taxes) === 0)) {
    return [];
  }

  const taxRows = normalizeTaxDetailsList(svc);
  if (shipping && hasSelectedId) {
    // A deliberate Shipping tax ID must not be superseded by a stale saved
    // taxDetailsList from a previous GST/HST selection.
    const matchingSaved = taxRows.filter((row) =>
      String(getTaxId(row)) === String(selectedId)
    );
    if (matchingSaved.length) return matchingSaved;
    const matchingCatalog = findTaxCatalogMatch(selectedId, allTaxes);
    if (matchingCatalog) return [matchingCatalog];
    return [buildGenericServiceTax(svc)].filter(Boolean);
  }
  if (taxRows.length) return taxRows;

  const matchedRows = getOtherServiceTaxIds(svc)
    .map((taxId) => findTaxCatalogMatch(taxId, allTaxes))
    .filter(Boolean);
  if (matchedRows.length) return matchedRows;

  return [buildGenericServiceTax(svc)].filter(Boolean);
};

const collectAllTaxes = (invoiceDetails = {}) => {
  const seed = [];
  if (Array.isArray(invoiceDetails?.allTaxes)) seed.push(...invoiceDetails.allTaxes);
  if (Array.isArray(invoiceDetails?.taxes)) seed.push(...invoiceDetails.taxes);
  if (Array.isArray(invoiceDetails?.taxOptions)) seed.push(...invoiceDetails.taxOptions);
  (Array.isArray(invoiceDetails?.items) ? invoiceDetails.items : []).forEach((item) => seed.push(...normalizeTaxDetailsList(item)));
  (Array.isArray(invoiceDetails?.otherServices) ? invoiceDetails.otherServices : []).forEach((svc) => seed.push(...normalizeTaxDetailsList(svc)));
  return seed;
};

const addBucketTax = (buckets, component, taxableSubtotalCents, taxCents) => {
  if (!component || taxCents === 0) return;
  const current = buckets.get(component.key) || {
    key: component.key,
    label: component.label,
    rate: component.rate,
    taxableSubtotalCents: 0,
    taxCents: 0,
    sourceTaxIds: [],
  };
  current.taxableSubtotalCents += taxableSubtotalCents;
  current.taxCents += taxCents;
  if (component.sourceTaxId != null && !current.sourceTaxIds.some((id) => String(id) === String(component.sourceTaxId))) {
    current.sourceTaxIds.push(component.sourceTaxId);
  }
  buckets.set(component.key, current);
};

const hasStandaloneAndCombinedGst = (taxRows = []) => {
  const rows = Array.isArray(taxRows) ? taxRows : [];
  return rows.some(isStandaloneGstTax) && rows.some(isCombinedCanadianGstPstRow);
};

const getTargetTaxCents = (invoiceDetails = {}, options = {}) => {
  const fromOptions = Number(options?.targetTaxCents);
  if (Number.isFinite(fromOptions)) return fromOptions;
  const fromTotals = Number(invoiceDetails?.totalsCents?.taxCents);
  if (Number.isFinite(fromTotals)) return fromTotals;
  const fromCanonical = Number(invoiceDetails?.taxAmount);
  if (Number.isFinite(fromCanonical)) return toCents(fromCanonical);
  return null;
};

const getAdjustmentTotalCents = (invoiceDetails = {}, options = {}) => {
  const explicit = Number(options?.adjustmentCents);
  if (Number.isFinite(explicit)) return explicit;

  const adjustments = Array.isArray(invoiceDetails?.adjustments)
    ? invoiceDetails.adjustments
    : Array.isArray(invoiceDetails?.Adjustments)
      ? invoiceDetails.Adjustments
      : [];

  return adjustments.reduce((sum, adjustment) => {
    const resolvedCents = Number(adjustment?.resolvedCents);
    if (Number.isFinite(resolvedCents)) return sum + resolvedCents;
    const resolvedAmount = Number(adjustment?.resolvedAmount);
    return sum + (Number.isFinite(resolvedAmount) ? toCents(resolvedAmount) : 0);
  }, 0);
};

const applyPreTaxAdjustmentsToBuckets = (rows = [], adjustmentCents = 0, commercialSubtotalCents = 0) => {
  const baseCommercialCents = Math.max(0, Number(commercialSubtotalCents) || 0);
  if (!rows.length || baseCommercialCents <= 0 || adjustmentCents >= 0) return rows;

  const discountCents = Math.min(baseCommercialCents, Math.abs(adjustmentCents));
  const ratio = Math.max(0, Math.min(1, (baseCommercialCents - discountCents) / baseCommercialCents));

  return rows.map((row) => {
    const originalTaxable = Math.max(0, Number(row?.taxableSubtotalCents) || 0);
    const adjustedTaxable = Math.round(originalTaxable * ratio);
    const rate = normalizeTaxRateDecimal(row?.rate);
    return {
      ...row,
      preAdjustmentTaxableSubtotalCents: originalTaxable,
      adjustmentTaxableSubtotalCents: adjustedTaxable - originalTaxable,
      taxableSubtotalCents: adjustedTaxable,
      taxCents: Math.round(adjustedTaxable * rate),
    };
  });
};

// Manual QA checklist for generated invoices:
// - Invoice with GST only
// - Invoice with GST/PSK only
// - Invoice with mixed GST and GST/PSK items
// - Invoice with Exempt item
// - Deposit/partial invoice
// - Confirm footer tax rows sum to existing tax total
// - Confirm total remains unchanged
export const buildInvoiceTaxBreakdown = (invoiceDetails = {}, options = {}) => {
  const allTaxes = collectAllTaxes(invoiceDetails);
  const resolveComponents = buildTaxComponentResolver(allTaxes);
  const buckets = new Map();

  (Array.isArray(invoiceDetails?.items) ? invoiceDetails.items : []).forEach((item, itemIndex) => {
    const selectedId = [item?.TaxId, item?.taxId, item?.taxID]
      .find((id) => id != null && id !== '');
    const explicitlyUntaxed = item?.taxable === false || item?.isTaxable === false ||
      item?.taxDetails?.taxable === false ||
      ['exempt', 'non-taxable', 'no-tax', 'none'].includes(
        String(item?.taxTreatment ?? item?.TaxTreatment ?? '').trim().toLowerCase()
      ) || (selectedId != null && Number(selectedId) === 0);
    if (explicitlyUntaxed) return;

    const taxableSubtotalCents = getItemSubtotalCents(item);
    const itemTaxRows = normalizeTaxDetailsList(item);
    if (hasStandaloneAndCombinedGst(itemTaxRows)) {
      warnTaxBreakdownDiagnostic('Item has both standalone GST and combined GST/PXX taxDetailsList entries.', {
        invoiceId: invoiceDetails?.id || invoiceDetails?.invoiceId || null,
        itemIndex,
        itemId: item?.id ?? item?.ProductId ?? item?.productId ?? null,
        taxDetailsList: itemTaxRows,
      });
    }
    if (taxableSubtotalCents <= 0) return;
    itemTaxRows.forEach((tax) => {
      const components = resolveComponents(tax);
      if (isCombinedCanadianGstPstRow(tax) && components.length !== 2) {
        warnTaxBreakdownDiagnostic('Combined GST/PXX tax row did not split into two components.', {
          invoiceId: invoiceDetails?.id || invoiceDetails?.invoiceId || null,
          itemIndex,
          tax,
          components,
        });
      }
      components.forEach((component) => {
        addBucketTax(buckets, component, taxableSubtotalCents, Math.round(taxableSubtotalCents * component.rate));
      });
    });
  });

  (Array.isArray(invoiceDetails?.otherServices) ? invoiceDetails.otherServices : []).forEach((svc) => {
    const taxableSubtotalCents = getOtherServiceSubtotalCents(svc);
    if (taxableSubtotalCents <= 0) return;
    const effectiveTaxRows = buildResolvedServiceTaxRows(svc, allTaxes);
    effectiveTaxRows.forEach((tax) => {
      const components = resolveComponents(tax);
      if (isCombinedCanadianGstPstRow(tax) && components.length !== 2) {
        warnTaxBreakdownDiagnostic('Combined other-service GST/PXX tax row did not split into two components.', {
          invoiceId: invoiceDetails?.id || invoiceDetails?.invoiceId || null,
          serviceIndex: svc?.id ?? null,
          tax,
          components,
        });
      }
      components.forEach((component) => {
        addBucketTax(buckets, component, taxableSubtotalCents, Math.round(taxableSubtotalCents * component.rate));
      });
    });
  });

  const adjustmentCents = getAdjustmentTotalCents(invoiceDetails, options);
  const commercialSubtotalCents = [
    ...(Array.isArray(invoiceDetails?.items) ? invoiceDetails.items : []).map(getItemSubtotalCents),
    ...(Array.isArray(invoiceDetails?.otherServices) ? invoiceDetails.otherServices : []).map(getOtherServiceSubtotalCents),
  ].reduce((sum, cents) => sum + Math.max(0, Number(cents) || 0), 0);
  const rows = applyPreTaxAdjustmentsToBuckets(
    Array.from(buckets.values()).filter((row) => row.taxCents > 0),
    adjustmentCents,
    commercialSubtotalCents
  );
  const targetTaxCents = getTargetTaxCents(invoiceDetails, options);
  if (rows.length && Number.isFinite(targetTaxCents)) {
    const actualTaxCents = rows.reduce((sum, row) => sum + row.taxCents, 0);
    const drift = targetTaxCents - actualTaxCents;
    if (drift !== 0 && Math.abs(drift) <= 5) {
      const largest = rows.reduce((max, row) => (row.taxCents > max.taxCents ? row : max), rows[0]);
      largest.taxCents += drift;
    }

    const reconciledTaxCents = rows.reduce((sum, row) => sum + row.taxCents, 0);
    if (reconciledTaxCents !== targetTaxCents) {
      warnTaxBreakdownDiagnostic('Tax breakdown cents do not match target tax cents after rounding reconciliation.', {
        invoiceId: invoiceDetails?.id || invoiceDetails?.invoiceId || null,
        targetTaxCents,
        summedTaxBreakdownCents: reconciledTaxCents,
        differenceCents: reconciledTaxCents - targetTaxCents,
        adjustmentCents,
        taxBreakdown: rows,
      });
    }
  }

  return rows;
};
